import { observer } from 'mobx-react';
import React from 'react';
import { BrowseNavigationItemSelected, BrowseNavigationItemStyle, BrowseNavigationItemType } from '@udemy/browse-event-tracking';
import { findFocusables } from '@udemy/design-system-utils';
import { Tracker } from '@udemy/event-tracking';
import { useI18n } from '@udemy/i18n';
import { MenuAimArea } from '@udemy/react-popup-components';
import { useUDData } from '@udemy/ud-data';
import { AGGREGATOR_NAV } from '../../browse-constants';
import { useBrowseNavStore } from '../../hooks/use-browse-nav-store';
import { useHeaderStore } from '../../hooks/use-header-store';
import { useUfbContext } from '../../hooks/use-ufb-context';
import { getUserTypes } from '../../utils/get-user-types';
import { HeaderButton, HeaderDropdown } from '../header-dropdown.react-component';
import styles from "./browse-nav.module.css";
import { BrowseNav } from './browse-nav.react-component';
import { GetCertifiedNavContent } from './get-certified-nav.react-component';

/** Focus cycle for full BrowseNav (level-one + trigger). */
export const getFocusCycle = container => {
  const browseNavLevel1 = container.querySelector('.js-browse-nav-level-one');
  const headerButton = container.querySelector('.js-header-button');
  if (browseNavLevel1 && headerButton) {
    const categories = findFocusables(browseNavLevel1);
    return categories.concat(headerButton);
  }
  return [];
};

/** Focus cycle for Learn AI–only dropdown (level-two + level-three + trigger). */
export const getFocusCycleLearnAI = container => {
  const levelTwo = container.querySelector('.js-learn-ai-nav-level-two');
  const levelThree = container.querySelector('.js-learn-ai-nav-level-three');
  const headerButton = container.querySelector('.js-header-button');
  if (!headerButton) return [];
  const focusables = [];
  if (levelTwo) focusables.push(...findFocusables(levelTwo));
  if (levelThree) focusables.push(...findFocusables(levelThree));
  return focusables.concat(headerButton);
};
export const BrowseNavDropdown = observer(({
  className
}) => {
  const browseNavStore = useBrowseNavStore();
  const headerStore = useHeaderStore();
  const ufbContext = useUfbContext();
  const {
    Config,
    me
  } = useUDData();
  const {
    user
  } = headerStore.userSpecificContext;
  const {
    gettext
  } = useI18n();
  const {
    isMXUser
  } = getUserTypes({
    config: Config,
    me,
    headerData: headerStore.userSpecificContext
  });
  const showSplitCategoriesNav = headerStore.isSplitNavByCategoriesEnabled && isMXUser;
  let text = gettext('Categories');
  const renderExploreButton = label => /*#__PURE__*/React.createElement(HeaderButton, null, label);
  const renderHeaderButton = () => {
    if (!user) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles['placeholder-header-button']
      }, gettext('Categories'));
    }
    const showUpdatedNavHeaderButton = user.consumer_subscription_active || (user === null || user === void 0 ? void 0 : user.show_updated_pp_and_ub_navigation) || (user === null || user === void 0 ? void 0 : user.show_updated_mx_navigation) || headerStore.isAggregatorFeatureEnabled || headerStore.isAcademiesFeatureEnabled;
    if (ufbContext !== null && ufbContext !== void 0 && ufbContext.browseNavDropdownText) {
      text = ufbContext.browseNavDropdownText;
    } else if (showSplitCategoriesNav) {
      text = gettext('Find Courses');
      return renderExploreButton(text);
    } else if (showUpdatedNavHeaderButton) {
      text = gettext('Explore');
    }
    return /*#__PURE__*/React.createElement(HeaderButton, null, text);
  };
  if (!user) {
    className += ` ${styles['placeholder-header-button']}`;
  }
  function publishSplitNavTriggerSelected(type, id) {
    Tracker.publishEvent(new BrowseNavigationItemSelected({
      type,
      id,
      style: BrowseNavigationItemStyle.NAVIGATION_DROPDOWN,
      uiRegion: 'main_nav'
    }));
  }
  if (showSplitCategoriesNav) {
    const findCoursesLabel = gettext('Find Courses');
    const getCertifiedLabel = gettext('Get Certified');
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HeaderDropdown, {
      trigger: /*#__PURE__*/React.createElement(HeaderButton, {
        onClick: () => publishSplitNavTriggerSelected(BrowseNavigationItemType.CATEGORY, 'find-courses')
      }, findCoursesLabel),
      className: className,
      componentClass: "nav",
      ariaLabel: findCoursesLabel,
      placement: "bottom-start",
      getFocusCycle: getFocusCycle
    }, /*#__PURE__*/React.createElement(BrowseNav, {
      browseNavStore: browseNavStore,
      headerStore: headerStore,
      ufbContext: ufbContext
    }), /*#__PURE__*/React.createElement(MenuAimArea, null)), /*#__PURE__*/React.createElement(HeaderDropdown, {
      trigger: /*#__PURE__*/React.createElement(HeaderButton, {
        onClick: () => {
          const item = AGGREGATOR_NAV(gettext).AGGREGATORS;
          publishSplitNavTriggerSelected(item.type, String(item.id));
        }
      }, getCertifiedLabel),
      className: `${className} ${styles['show-on-large-screen']}`,
      componentClass: "nav",
      ariaLabel: getCertifiedLabel,
      placement: "bottom-start"
    }, /*#__PURE__*/React.createElement(GetCertifiedNavContent, {
      browseNavStore: browseNavStore,
      headerStore: headerStore
    }), /*#__PURE__*/React.createElement(MenuAimArea, null)));
  }
  return /*#__PURE__*/React.createElement(HeaderDropdown, {
    trigger: renderHeaderButton(),
    className: className,
    componentClass: "nav",
    ariaLabel: text,
    placement: "bottom-start",
    getFocusCycle: getFocusCycle
  }, /*#__PURE__*/React.createElement(BrowseNav, {
    browseNavStore: browseNavStore,
    headerStore: headerStore,
    ufbContext: ufbContext
  }), /*#__PURE__*/React.createElement(MenuAimArea, null));
});
//# sourceMappingURL=browse-nav-dropdown.react-component.js.map